import * as React from 'react';

import classnames from 'classnames';

import {formEncodedApi} from 'nutshell-core/api';
import type {User} from 'nutshell-core/types';

import {EngagementIcon} from 'shells/icon/svg/engagement-icon';

import {playNotificationSound} from './helpers';
import {getIconSize} from './sidebar';
import {SidebarIcon} from './sidebar-icon';
import {SidebarNavLink} from './sidebar-nav-link';

type Props = {
    inboxUrl: string;
    isMainSidebarCollapsed: boolean;
    isActiveTab: boolean;
    loggedInUser: User | null | undefined;
};

const NOTIFICATION_POLLING_INTERVAL = 30000;
const FLASHING_TITLE_INTERVAL = 3000;

// This specific nav item has its own component because of the notification logic
// that it houses. Logic lives here for polling for notifications, as well as
// playing sounds, showing the flashing tab banner, etc.
export function EngagementSidebarItem(props: Props) {
    const [inboxNotificationCount, setInboxNotificationCount] = React.useState(0);

    // Starts null so the first fetch (page load) never triggers a sound.
    const previousCountRef = React.useRef<number | null>(null);

    const notificationPollingIntervalId = React.useRef(null);
    React.useEffect(() => {
        if (
            NutClientConfig.shouldPollForInboxNotifications &&
            props.loggedInUser &&
            props.loggedInUser.permissions.canUseOrTryInbox
        ) {
            const checkNotifications = () => {
                formEncodedApi
                    .get('/notification/inbox-count.json')
                    .then((res) => {
                        // @ts-expect-error
                        return res.json();
                    })
                    .then((json) => {
                        const unread = json.seen + json.unseen;

                        if (
                            previousCountRef.current !== null &&
                            unread > previousCountRef.current
                        ) {
                            if (NutClientConfig.shouldPlayEngagementNotificationSounds) {
                                playNotificationSound();
                            }
                        }

                        previousCountRef.current = unread;
                        setInboxNotificationCount(unread);
                    })
                    .catch((err) => {
                        if (err.httpResponse.status.toString().startsWith('4')) {
                            // @ts-expect-error
                            clearInterval(notificationPollingIntervalId.current);
                            notificationPollingIntervalId.current = null;
                        }
                    });
            };

            checkNotifications();

            // @ts-expect-error
            notificationPollingIntervalId.current = setInterval(
                checkNotifications,
                NOTIFICATION_POLLING_INTERVAL
            );
        }

        return () => {
            if (notificationPollingIntervalId.current) {
                clearInterval(notificationPollingIntervalId.current);
                notificationPollingIntervalId.current = null;
            }
        };
    }, [props.isActiveTab, props.loggedInUser]);

    // Flashing tab notifications
    const flashingTitleIntervalId = React.useRef(null);
    const notificationTitle = React.useRef(null);
    const storedPageTitleForFlashingTitle = React.useRef(null);
    const flashingNotificationIsActive = React.useRef(false);
    React.useEffect(() => {
        if (inboxNotificationCount) {
            // @ts-expect-error
            flashingTitleIntervalId.current = setInterval(() => {
                // if the actual page title has changed, save it to the stored page title for flashing title ref
                if (document.title !== storedPageTitleForFlashingTitle.current) {
                    if (!document.title.includes('unread message')) {
                        // @ts-expect-error
                        storedPageTitleForFlashingTitle.current = document.title;
                    }
                }

                // set the notification title based on the current count
                // @ts-expect-error
                notificationTitle.current = `(${inboxNotificationCount}) unread ${
                    inboxNotificationCount === 1 ? 'message' : 'messages'
                } · Nutshell`;

                // show/hide the title based on the current state
                if (flashingNotificationIsActive.current) {
                    if (storedPageTitleForFlashingTitle.current) {
                        document.title = storedPageTitleForFlashingTitle.current;
                        flashingNotificationIsActive.current = false;
                    }
                } else if (notificationTitle.current) {
                    document.title = notificationTitle.current;
                    flashingNotificationIsActive.current = true;
                }
            }, FLASHING_TITLE_INTERVAL);
        } else {
            // clear the page if there are no notifications
            // @ts-expect-error
            clearInterval(flashingTitleIntervalId.current);
            flashingTitleIntervalId.current = null;

            return;
        }

        return () => {
            if (flashingTitleIntervalId.current) {
                clearInterval(flashingTitleIntervalId.current);
                flashingTitleIntervalId.current = null;
            }
        };
    }, [inboxNotificationCount, storedPageTitleForFlashingTitle]);

    return (
        <SidebarNavLink
            isActive={props.isActiveTab}
            onBasePath={props.isActiveTab}
            href={props.inboxUrl}
            to={props.inboxUrl}
            tooltipText={props.isMainSidebarCollapsed ? 'Engagement' : undefined}
        >
            <SidebarIcon
                icon={EngagementIcon}
                size={getIconSize('engagement')}
                label='Engagement'
            />
            <span
                id='sidebar-notifications-badge'
                data-count={inboxNotificationCount}
                className={classnames('nut-sidebar--notification-badge--new', {
                    collapsed: props.isMainSidebarCollapsed,
                })}
            ></span>
        </SidebarNavLink>
    );
}
